import { debounce } from '@mui/material'
import api from 'api'
import { makeSubscribable } from 'helpers'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import TextField from 'components/Common/TextField'
import { Typography } from '@mui/material'
import moment from 'moment'
import { faCheckCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import ThreeDotsLoadingSpinner from 'components/Common/LoadingSpinner/threeDots'

const PromoCodeField = ({ onCode, onLoading, errorSet, planSelected, stripePromotionCode, value, valueSet }) => {
  const [state, setState] = useState(null)
  const [subscribable, setSubscribable] = useState(null)
  const today = new Date()

  const onTextChangeDebouced = useCallback(
    value => {
      if (value) {
        const promise = api.getPromotionCode(value)
        const subscribable = makeSubscribable(promise)
        setSubscribable(subscribable)
      }
    },
    [],
  )

  const onChangeDebounced = useMemo(() => debounce(onTextChangeDebouced, 1000), [onTextChangeDebouced])

  const onTextChange = useCallback(
    event => {
      const nextValue = event.currentTarget.value
      valueSet(nextValue)
      setState('typing')
      onChangeDebounced(nextValue)

      if (nextValue === '') {
        onCode(null)
      }
    },
    [onChangeDebounced, onCode, valueSet],
  )

  const onCodeLoadSuccess = useCallback(
    stripePromotionCode => {
      if (stripePromotionCode === null) {
        setState('error')
        onCode(null)
      } else {
        setState('success')
        onCode(stripePromotionCode)
      }
      onLoading(false)
    },
    [onCode, onLoading],
  )

  useEffect(() => {
    if (stripePromotionCode?.active === false) {
      errorSet(true)
      setState('error')
    } else if (planSelected?.title === 'Annually' && stripePromotionCode?.metadata?.SubscriptionType === 'monthly') {
      errorSet(true)
      setState('error')
    } else {
      errorSet(false)
    }
  }, [errorSet, planSelected?.title, stripePromotionCode])

  const onCodeLoadError = useCallback(error => {
    setState('error')
  }, [])

  useEffect(() => {
    if (subscribable !== null) {
      setState('loading')
      onLoading(true)
      return subscribable.subscribe(onCodeLoadSuccess, onCodeLoadError)
    }
  }, [onCodeLoadError, onCodeLoadSuccess, onLoading, subscribable])

  useEffect(() => {
    if (state === 'typing') {
      onLoading(true)
      onCode(null)
    }
  }, [onCode, onLoading, state])

  const errorState = (stripePromotionCode?.active === false && moment.unix(stripePromotionCode?.expires_at)?._d < today) ||
    (planSelected?.title === 'Annually' && stripePromotionCode?.metadata?.SubscriptionType === 'monthly')

  const errorMessage = (stripePromotionCode?.active === false &&
    moment.unix(stripePromotionCode?.expires_at)?._d < today &&
    'Unfortunately this promo code is no longer valid') ||
    (planSelected?.title === 'Annually' &&
      stripePromotionCode?.metadata?.SubscriptionType === 'monthly' &&
      'This promo code is only valid for monthly subscriptions')

  return (
    <TextField
      label="Promo Code"
      placeholder="Promo Code"
      fullWidth
      bgwhite
      error={errorState}
      helperText={
        <Typography variant="caption">
          {errorMessage}
        </Typography>
      }
      onChange={onTextChange}
      value={value}
      InputProps={{
        endAdornment: (
          <div>
            {state === 'loading' && <ThreeDotsLoadingSpinner width="60px" />}
            {state === 'error' && <FontAwesomeIcon icon={faTimesCircle} size="2x" color="#FF5A5F" />}
            {state === 'success' && <FontAwesomeIcon icon={faCheckCircle} size="2x" color="#5DD9C1" />}
          </div>
        ),
      }}
    />
  )
}

export default PromoCodeField
